import { motion, useReducedMotion } from "framer-motion";
import { growthStages } from "@/content/site";
import { Eyebrow, SectionHeading } from "@/components/primitives";
import { Reveal } from "@/components/motion/Reveal";
import { Search, Megaphone, CalendarCheck, TrendingUp } from "lucide-react";

const icons = [Search, Megaphone, CalendarCheck, TrendingUp];

export default function GrowthSystem() {
  const reduce = useReducedMotion();

  return (
    <section
      data-testid="growth-system-section"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>The Green Circle Group Growth System</Eyebrow>
          <SectionHeading className="mt-5">
            From getting found to getting booked—one connected system.
          </SectionHeading>
          <p className="mt-6 text-lg md:text-xl text-mid-neutral leading-relaxed">
            Digital growth works best when every step supports the next. Green
            Circle connects the customer journey instead of treating marketing
            as a collection of unrelated tasks.
          </p>
        </div>

        {/* Desktop orbital diagram */}
        <div className="mt-20 hidden lg:block">
          <div className="relative mx-auto" style={{ width: 720, height: 720 }}>
            {/* connecting circle that draws itself */}
            <svg
              aria-hidden="true"
              viewBox="0 0 720 720"
              className="absolute inset-0 w-full h-full"
            >
              <motion.circle
                cx="360"
                cy="360"
                r="250"
                fill="none"
                stroke="#DCE6E1"
                strokeWidth="2"
                strokeDasharray="10 12"
                initial={reduce ? {} : { pathLength: 0 }}
                whileInView={reduce ? {} : { pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
            </svg>

            {/* center */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center w-56">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-forest text-lime mb-4">
                <TrendingUp className="h-9 w-9" />
              </div>
              <p className="text-forest font-extrabold text-xl leading-tight">
                One connected growth cycle
              </p>
              <p className="text-mid-neutral text-sm mt-2">
                Each stage feeds the next—continually.
              </p>
            </div>

            {/* nodes */}
            {growthStages.map((stage, i) => {
              const Icon = icons[i];
              const positions = [
                { top: 0, left: "50%", tx: "-50%", ty: "0" },
                { top: "50%", left: "100%", tx: "-100%", ty: "-50%" },
                { top: "100%", left: "50%", tx: "-50%", ty: "-100%" },
                { top: "50%", left: 0, tx: "0", ty: "-50%" },
              ];
              const p = positions[i];
              return (
                <motion.div
                  key={stage.title}
                  className="absolute w-64"
                  style={{
                    top: p.top,
                    left: p.left,
                    transform: `translate(${p.tx}, ${p.ty})`,
                  }}
                  initial={reduce ? {} : { opacity: 0, scale: 0.85 }}
                  whileInView={reduce ? {} : { opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.25 }}
                >
                  <div className="bg-white rounded-3xl border border-border-neutral p-6 shadow-[0_10px_40px_rgba(3,34,26,0.06)]">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="text-xs font-bold tracking-[0.2em] text-mid-neutral">
                        {stage.step}
                      </span>
                    </div>
                    <h3 className="mt-4 text-xl font-extrabold text-forest">
                      {stage.title}
                    </h3>
                    <p className="mt-2 text-sm text-mid-neutral leading-relaxed">
                      {stage.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile / tablet vertical timeline */}
        <div className="mt-16 lg:hidden relative">
          <div
            aria-hidden="true"
            className="absolute left-[27px] top-4 bottom-4 w-0.5 border-l-2 border-dashed border-border-neutral"
          />
          <div className="space-y-6">
            {growthStages.map((stage, i) => {
              const Icon = icons[i];
              return (
                <Reveal
                  key={stage.title}
                  delay={i * 0.08}
                  className="relative flex gap-5"
                >
                  <div className="relative z-10 shrink-0 inline-flex h-14 w-14 items-center justify-center rounded-full bg-forest text-lime">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="bg-white rounded-2xl border border-border-neutral p-5 flex-1">
                    <span className="text-xs font-bold tracking-[0.2em] text-mid-neutral">
                      {stage.step}
                    </span>
                    <h3 className="mt-1 text-lg font-extrabold text-forest">
                      {stage.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-mid-neutral leading-relaxed">
                      {stage.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
